import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawRopeSpan, drawTimberBoards } from './propDrawingPrimitives';
import { drawFishingNet, drawRopeCoil } from './maritimeDrawingPrimitives';

export type HarborEquipmentTemplate = 'harbor_cargo_hoist' | 'sail_repair_rack';

/** 160×188. An open braced boom, iron pulley and free hook above grounded cargo. */
function cargoHoist(p: TownPropPalette): string {
  return `<g data-part="harbor-cargo-hoist">
    <path d="M16 178L45 115L70 178" fill="none" stroke="${hex(p.wood)}" stroke-width="7" stroke-linejoin="round" />
    <path d="M17 176L45 118L68 176" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
    ${drawTimberBoards({ x: 38, y: 10, width: 20, height: 168 }, p, { axis: 'vertical', boards: 2 })}
    <path d="M49 20L131 40L53 91" fill="none" stroke="${hex(p.wood)}" stroke-width="9" stroke-linejoin="round" />
    <path d="M53 25L132 43 M53 86L131 43" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2.2" />
    ${drawTimberBoards({ x: 30, y: 14, width: 112, height: 13 }, p, { axis: 'horizontal', boards: 2 })}
    <path d="M34 14H42V27H34Z M124 14H133V27H124Z M35 102H61V111H35Z M35 157H61V166H35Z"
      fill="${hex(p.iron)}" />
    <path d="M39 4H57L62 10H34Z" fill="${hex(p.woodEdge)}" />
    <circle cx="128" cy="34" r="9" fill="${hex(p.iron)}" />
    <circle cx="128" cy="34" r="5.5" fill="${hex(p.stone)}" />
    <circle cx="128" cy="34" r="2" fill="${hex(p.iron)}" />
    <g data-part="hoist-fall-line">
      ${drawRopeSpan({ x: 121, y: 39 }, { x: 121, y: 93 }, p, { sag: 0, thickness: 2.4 })}
      ${drawRopeSpan({ x: 135, y: 39 }, { x: 135, y: 93 }, p, { sag: 0, thickness: 2.4 })}
      <path d="M118 92H138V99H118Z" fill="${hex(p.iron)}" />
      <path d="M128 99V107Q143 115 132 123Q122 126 120 117" fill="none"
        stroke="${hex(p.iron)}" stroke-width="4" stroke-linecap="round" />
      <path d="M129 107Q138 114 132 119" fill="none" stroke="${hex(p.stone)}" stroke-width="1.2" />
    </g>
    <circle cx="48" cy="124" r="14" fill="${hex(p.iron)}" />
    <circle cx="48" cy="124" r="10" fill="${hex(p.woodEdge)}" />
    <circle cx="48" cy="124" r="5" fill="${hex(p.wood)}" />
    <path d="M48 124L25 138L21 133" fill="none" stroke="${hex(p.iron)}" stroke-width="4" stroke-linecap="round" />
    <g data-part="hoist-ground-crate">
      ${drawTimberBoards({ x: 101, y: 145, width: 51, height: 43 }, p, { axis: 'horizontal', boards: 4 })}
      <path d="M101 145H107V188H101Z M146 145H152V188H146Z" fill="${hex(p.woodEdge)}" />
      <path d="M108 149L145 184 M145 149L108 184" stroke="${hex(p.woodEdge)}" stroke-width="4" />
      <path d="M103 147H105 M148 147H150 M103 185H105 M148 185H150" stroke="${hex(p.iron)}" stroke-width="2" />
      ${drawRopeCoil({ x: 111, y: 133, width: 31, height: 12 }, p, { turns: 3 })}
    </g>
    <path d="M7 178H82V184H7Z M12 184H77V188H12Z" fill="${hex(p.stone)}" />
    <path d="M10 178H80V181H10Z" fill="${hex(p.iron)}" />
    <path d="M18 182H26 M62 182H70" stroke="${hex(p.woodEdge)}" stroke-width="1.5" />
  </g>`;
}

/** 144×152. Separate net and patched sail hang from a pegged repair frame. */
function sailRack(p: TownPropPalette): string {
  const posts = [13, 117].map(x => `<g data-part="repair-rack-post">
    ${drawTimberBoards({ x, y: 8, width: 14, height: 140 }, p, { axis: 'vertical', boards: 2 })}
    <path d="M${x - 4} 146H${x + 18}V152H${x - 4}Z" fill="${hex(p.iron)}" />
    <path d="M${x + 2} 12H${x + 12} M${x + 2} 142H${x + 12}" stroke="${hex(p.woodEdge)}" stroke-width="2" />
  </g>`).join('');
  return `<g data-part="sail-repair-rack">
    <path d="M22 39L44 18 M122 39L100 18" stroke="${hex(p.wood)}" stroke-width="7" />
    ${drawFishingNet({ x: 28, y: 28, width: 47, height: 82 }, p, { columns: 4, rows: 7 })}
    <g data-part="patched-sail">
      <path d="M80 25H114L118 104L101 114L79 106L83 67Z" fill="${hex(p.glassCore)}" />
      <path d="M84 27L87 102 M110 27L114 102 M82 67L115 69" fill="none" stroke="${hex(p.glass)}" stroke-width="1.4" />
      <path d="M91 67L105 69L104 85L90 83Z" fill="${hex(p.roofEdge)}" />
      <path d="M92 69L103 71L102 83L92 81Z" fill="none" stroke="${hex(p.wood)}" stroke-width="1" stroke-dasharray="2 2" />
      <path d="M80 104L101 111L117 102" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.6" />
    </g>
    ${posts}
    ${drawTimberBoards({ x: 5, y: 12, width: 134, height: 10 }, p, { axis: 'horizontal', boards: 1 })}
    <path d="M9 12H135" stroke="${hex(p.woodEdge)}" stroke-width="1.5" />
    ${[33, 67, 86, 108].map(x => `<path d="M${x} 17V29" stroke="${hex(p.iron)}" stroke-width="2.5" />`).join('')}
    <path d="M23 127H121V134H23Z" fill="${hex(p.wood)}" />
    <path d="M24 128H120" stroke="${hex(p.woodEdge)}" stroke-width="1.4" />
    ${drawRopeCoil({ x: 35, y: 113, width: 34, height: 14 }, p, { turns: 4 })}
    <path d="M85 126L96 116L101 120L91 126Z" fill="${hex(p.iron)}" />
    <path d="M95 116L101 110" stroke="${hex(p.woodEdge)}" stroke-width="3" stroke-linecap="round" />
  </g>`;
}

export function buildHarborEquipmentBody(template: HarborEquipmentTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'harbor_cargo_hoist': return cargoHoist(palette);
  case 'sail_repair_rack': return sailRack(palette);
  }
}
